// Server-backed persistence for admin edits to plans + capabilities.
//
// Edits made here PATCH the signal server (`/admin/plans/:tier`,
// `/admin/capabilities`) so they land in the plan_overrides /
// capability_overrides tables. The brochure reads the merged result from
// `/public/plans` + `/public/capabilities`, which is how an admin price
// change shows up on the public site after a refresh — no redeploy.
//
// This file used to be a localStorage-only dev placeholder; edits never
// left the browser, which is why brochure changes never appeared. It is
// now the real fetch-backed layer the placeholder always promised.
//
// Plans are the growth-spec ladder (lib/tiers.ts): Free, Pro, Pro+, Team and
// Enterprise. Capabilities are the catalogue (lib/capabilityCatalog.ts), with
// Pro+ and Enterprise columns whose defaults inherit as tierDefault() says until
// a row or an override states otherwise.
import { useCallback, useEffect, useRef, useState } from 'react'
import { PLANS as SHARED_PLANS } from '@shared/plans'
import { CAPABILITIES, Capability, CapabilityDefaults } from './capabilityCatalog'
import { TIER_LADDER, TierId, TierSpec } from './tiers'
import {
  clearCapabilityOverrides,
  clearPlanOverrides,
  listCapabilityOverrides,
  listPlanOverrides,
  patchPlan as apiPatchPlan,
  upsertCapability as apiUpsertCapability
} from './adminClient'

// One plan as the admin edits it: the ladder's contract numbers plus the
// brochure fields an override can change.
export interface AdminPlan extends TierSpec {
  tagline: string
  active: boolean
}

// The fields an override row can carry (PATCH /admin/plans/:tier).
export interface PlanPatch {
  priceMonthly?: number
  priceYearly?: number
  tagline?: string
  active?: boolean
}

export type PlanOverrides = Record<string, PlanPatch>
export type CapabilityOverrides = Record<string, Partial<Record<TierId, boolean | number | string>>>

// ── Server-shape → local-shape mappers ──────────────────────────────────
// The server returns every override column, using null for "unset". We
// strip nulls so a spread over the shared default leaves that field at its
// default (spreading `{ priceMonthly: null }` would otherwise null it out).
function planOverridesFromServer(
  rows: { tier: string; priceMonthly: number | null; priceYearly: number | null; tagline: string | null; active: boolean | null }[]
): PlanOverrides {
  const out: PlanOverrides = {}
  for (const r of rows) {
    const patch: PlanPatch = {}
    if (r.priceMonthly !== null) patch.priceMonthly = r.priceMonthly
    if (r.priceYearly !== null) patch.priceYearly = r.priceYearly
    if (r.tagline !== null) patch.tagline = r.tagline
    if (r.active !== null) patch.active = r.active
    if (Object.keys(patch).length > 0) out[r.tier] = patch
  }
  return out
}

function capabilityOverridesFromServer(
  rows: { capabilityKey: string; tier: string; value: boolean | number | string }[]
): CapabilityOverrides {
  const out: CapabilityOverrides = {}
  for (const r of rows) {
    if (!out[r.capabilityKey]) out[r.capabilityKey] = {}
    ;(out[r.capabilityKey] as Record<string, unknown>)[r.tier] = r.value
  }
  return out
}

// ── Merge helpers — shared defaults overlaid with admin overrides ────────
// A tier's default tagline: the shared brochure copy where the shared module
// has the tier, else the ladder's own summary (Pro+ and Enterprise are new).
function defaultTagline(t: TierSpec): string {
  return SHARED_PLANS.find((p) => (p.id as string) === t.id)?.tagline ?? t.summary
}

export function mergePlans(overrides: PlanOverrides): AdminPlan[] {
  return TIER_LADDER.map((t) => {
    const o = overrides[t.id] ?? {}
    return {
      ...t,
      priceMonthly: o.priceMonthly ?? t.priceMonthly,
      priceYearly: o.priceYearly ?? t.priceYearly,
      tagline: o.tagline ?? defaultTagline(t),
      active: o.active ?? true
    }
  })
}

export function mergeCapabilities(overrides: CapabilityOverrides): Capability[] {
  return CAPABILITIES.map((c) => ({
    ...c,
    defaults: { ...c.defaults, ...overrides[c.key] } as CapabilityDefaults
  }))
}

export interface ServerOverridesState {
  plans: AdminPlan[]
  capabilities: Capability[]
  planOverrides: PlanOverrides
  capOverrides: CapabilityOverrides
  loading: boolean
  error: string | null
  saving: boolean
  // Briefly true (~2.5s) right after a successful save — drives the
  // "✓ Saved" confirmation so the operator gets unmistakable feedback.
  justSaved: boolean
  patchPlan: (planId: TierId, patch: PlanPatch) => Promise<void>
  patchCapability: (capKey: string, tier: TierId, value: boolean | number | string) => Promise<void>
  resetAll: () => Promise<void>
  reload: () => Promise<void>
}

// The single hook the Subscriptions page uses. Loads overrides from the
// server on mount, applies edits optimistically, and reverts + surfaces an
// error if the server rejects the write.
export function useServerOverrides(): ServerOverridesState {
  const [planOverrides, setPlanOverrides] = useState<PlanOverrides>({})
  const [capOverrides, setCapOverrides] = useState<CapabilityOverrides>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Flash the "✓ Saved" confirmation for 2.5s. Clears any pending timer so
  // rapid edits keep the banner up rather than flickering.
  const flashSaved = useCallback(() => {
    if (savedTimer.current) clearTimeout(savedTimer.current)
    setJustSaved(true)
    savedTimer.current = setTimeout(() => setJustSaved(false), 2500)
  }, [])

  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current) }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    const [planRes, capRes] = await Promise.all([listPlanOverrides(), listCapabilityOverrides()])
    if (!planRes.ok) {
      setError(`Couldn't load plan overrides: ${planRes.error}`)
      setLoading(false)
      return
    }
    if (!capRes.ok) {
      setError(`Couldn't load capability overrides: ${capRes.error}`)
      setLoading(false)
      return
    }
    setPlanOverrides(planOverridesFromServer(planRes.data.overrides))
    setCapOverrides(capabilityOverridesFromServer(capRes.data.overrides))
    setLoading(false)
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const patchPlan = useCallback(
    async (planId: TierId, patch: PlanPatch) => {
      const prev = planOverrides
      // Optimistic merge so the matrix updates instantly.
      const next: PlanOverrides = { ...prev, [planId]: { ...prev[planId], ...patch } }
      setPlanOverrides(next)
      setSaving(true)
      setError(null)
      const body: Record<string, unknown> = {}
      if (patch.priceMonthly !== undefined) body.priceMonthly = patch.priceMonthly
      if (patch.priceYearly !== undefined) body.priceYearly = patch.priceYearly
      if (patch.tagline !== undefined) body.tagline = patch.tagline
      if (patch.active !== undefined) body.active = patch.active
      const res = await apiPatchPlan(planId, body)
      setSaving(false)
      if (!res.ok) {
        setPlanOverrides(prev) // revert
        setError(`Couldn't save ${planId} plan: ${res.error}`)
      } else {
        flashSaved()
      }
    },
    [planOverrides, flashSaved]
  )

  const patchCapability = useCallback(
    async (capKey: string, tier: TierId, value: boolean | number | string) => {
      const prev = capOverrides
      const next: CapabilityOverrides = { ...prev, [capKey]: { ...prev[capKey], [tier]: value } }
      setCapOverrides(next)
      setSaving(true)
      setError(null)
      const res = await apiUpsertCapability({ capabilityKey: capKey, tier, value })
      setSaving(false)
      if (!res.ok) {
        setCapOverrides(prev) // revert
        setError(`Couldn't save ${capKey} (${tier}): ${res.error}`)
      } else {
        flashSaved()
      }
    },
    [capOverrides, flashSaved]
  )

  const resetAll = useCallback(async () => {
    const prevPlans = planOverrides
    const prevCaps = capOverrides
    setPlanOverrides({})
    setCapOverrides({})
    setSaving(true)
    setError(null)
    const [planRes, capRes] = await Promise.all([clearPlanOverrides(), clearCapabilityOverrides()])
    setSaving(false)
    if (!planRes.ok || !capRes.ok) {
      setPlanOverrides(prevPlans)
      setCapOverrides(prevCaps)
      setError(`Couldn't reset overrides: ${!planRes.ok ? planRes.error : capRes.ok ? '' : capRes.error}`)
    }
  }, [planOverrides, capOverrides])

  return {
    plans: mergePlans(planOverrides),
    capabilities: mergeCapabilities(capOverrides),
    planOverrides,
    capOverrides,
    loading,
    error,
    saving,
    justSaved,
    patchPlan,
    patchCapability,
    resetAll,
    reload
  }
}
